/**
 * AutismDecisionCard — a single Yes/No decision row, shared across pathways.
 *
 * Originally cloned from the ADHD PAD `DecisionCard` (6_PAD_TOC.js): a
 * fixed-width question label column on the left, two fixed-width (w-64)
 * Yes/No pills on the right. Selected pills are brand-filled with a tick;
 * unselected pills are a dashed outline with a plus.
 *
 * The `variant` prop controls the selected-pill colour so the same component
 * can be used on both the Autism pathway (plum/primary) and the ADHD pathway
 * (violet) while visually matching each pathway's existing Next Steps panels.
 * Default is `"autism"` for backward compatibility — all existing call sites
 * that omit the prop keep the original plum styling.
 *
 * Selecting the already-selected option clears it back to "unanswered" (null),
 * matching the ADHD toggle behaviour.
 *
 * @param {object}   props
 * @param {string}   props.question     The decision question text.
 * @param {string}   [props.description] Optional sub-caption under the label.
 * @param {boolean|null} props.value    Current value (true = Yes, false = No, null = unanswered).
 * @param {(v: boolean|null) => void} props.onChange  Selection handler.
 * @param {boolean}  [props.disabled]   Disable the pills (read-only / preview).
 * @param {string}   [props.tooltip]    Optional info-tooltip text shown to the
 *                                      left of the question. The tooltip icon is
 *                                      placed outside the opacity wrapper so it
 *                                      remains fully visible even when inactive.
 * @param {boolean}  [props.inactive]   Dims the entire row (question + pills)
 *                                      to indicate the decision is inapplicable
 *                                      for this patient (e.g. wrong contract).
 *                                      Behaves like disabled but with visual
 *                                      dimming; the tooltip remains legible.
 * @param {string}   [props.variant="autism"] Visual variant: "autism" uses
 *                                      plum/primary fill; "adhd" uses violet
 *                                      fill matching the ADHD PAD panels.
 */

import React from 'react';
import { CheckIcon, PlusIcon } from '@heroicons/react/solid';
import InfoTooltip from '../InfoTooltip';

// Variant-to-Tailwind mapping for the selected-pill background and border.
// "autism" (default) uses the plum brand primary; "adhd" uses violet to match
// the ADHD PAD DecisionCard styling in 6_PAD_TOC.js.
const VARIANT_CLASSES = {
  autism: 'bg-primary border-2 border-primary',
  adhd: 'bg-violet-600 border-2 border-violet-800',
};

export default function AutismDecisionCard({
  question,
  description,
  value,
  onChange,
  disabled = false,
  tooltip,
  inactive = false,
  variant = 'autism',
}) {
  const isDisabled = disabled || inactive;
  const selectedClasses = VARIANT_CLASSES[variant] || VARIANT_CLASSES.autism;

  const options = [
    { label: 'Yes', matches: true },
    { label: 'No', matches: false },
  ];

  return (
    <div className="mb-4 flex items-start">
      {/* Tooltip icon — lives outside the dim container so it is always fully
          visible and interactive even when the question row is inactive.
          A fixed-width spacer is used when there is no tooltip so alignment
          is preserved across all rows in the panel. */}
      <div className="w-5 shrink-0 mt-0.5">
        {tooltip && (
          <InfoTooltip
            content={tooltip}
            label={`Information about ${question}`}
            maxWidth="300px"
          />
        )}
      </div>

      {/* Question label + option pills — dimmed when inactive */}
      <div className={`flex items-start transition-opacity duration-200 ${inactive ? 'opacity-50' : ''}`}>
        <div className="w-[22rem] shrink-0 ml-2">
          <span className="text-left font-semibold text-gray-700">{question}</span>
          {description && (
            <p className="text-xs text-gray-400 italic mt-0.5">{description}</p>
          )}
        </div>

        <div className="ml-5 shrink-0 flex flex-col gap-1.5">
          <ul className="items-center flex flex-row gap-4">
            {options.map((option) => {
              const isSelected = value === option.matches;
              return (
                <li
                  key={option.label}
                  onClick={() => !isDisabled && onChange(isSelected ? null : option.matches)}
                  className={`
                    p-2 w-64 shrink-0
                    rounded-lg transition-all duration-300 ease-in-out
                    text-center
                    flex flex-row items-center justify-center gap-2
                    ${isDisabled ? 'cursor-not-allowed opacity-75' : 'cursor-pointer group'}
                    ${isSelected
                      ? `${selectedClasses} font-semibold shadow-lg transform scale-105`
                      : !isDisabled
                        ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
                        : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                    }
                  `}
                >
                  <div className={`transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                    {isSelected ? <CheckIcon className="w-4 h-4" /> : <PlusIcon className="w-4 h-4" />}
                  </div>
                  <span className={`font-semibold transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                    {option.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
